import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "@reduxjs/toolkit";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { Input } from "@App/Components/common/Input";
import { QuestionMarkCircleIcon } from "@App/Components/common/Icons";
import { SubmitButton } from "@App/Components/atoms/whitelabel/home/SubmitButton";
import { fleetApi } from "@App/apis/graphql/fleet/fleetApi";
import { actionCreators, type State } from "@App/state";
import { ContractCertificate } from "@App/Components/ClientSpecific/MobileGarantie/ContractCertificate";

/** A Garantienummer looks like `XXX-XXXXXX`, where X is a digit or a letter. */
const CONTRACT_NUMBER_PATTERN = /^[A-Za-z0-9]{3}-[A-Za-z0-9]{6}$/;
const CONTRACT_NUMBER_LENGTH = 10;

interface ContractNumberStepProps {
  /** Called after a vehicle was found, so the engine and brand steps can be skipped. */
  onVehicleFound: () => void;
  /** Called when the driver does not know their Garantienummer. */
  onSkip: () => void;
}

export function ContractNumberStep({ onVehicleFound, onSkip }: ContractNumberStepProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    setContractNumber,
    chooseBrand,
    changeBrandFilter,
    chooseGeneration,
    chooseMotorization,
    changeBevSupportFilter,
  } = bindActionCreators(actionCreators, dispatch);

  const organization = useSelector((state: State) => state.orders.organization);
  const persistedContractNumber = useSelector((state: State) => state.orders.contractNumber);

  const [contractNumber, setContractNumberInput] = useState(persistedContractNumber ?? "");
  const [validationMessage, setValidationMessage] = useState<string | undefined>(undefined);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const helpTitle = t("contractNumberStep.help.trigger");

  const handleChange = (value: string) => {
    setContractNumberInput(value.toUpperCase());
    setValidationMessage(undefined);
    setLookupError(null);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (loading) return;

    const trimmed = contractNumber.trim();
    if (!CONTRACT_NUMBER_PATTERN.test(trimmed)) {
      setValidationMessage(t("contractNumberStep.errors.format"));
      return;
    }

    setValidationMessage(undefined);
    setLookupError(null);
    setLoading(true);

    const result = await fleetApi.vehicle.getVehicle({
      organizationId: String(organization.id),
      contractNumber: trimmed,
    });

    setLoading(false);

    if (!result.ok) {
      setLookupError(t("contractNumberStep.errors.notFound"));
      return;
    }

    const { brand, generation, motorization, isBev } = result.vehicle;

    setContractNumber(trimmed);
    changeBevSupportFilter(isBev);
    chooseBrand(brand);
    changeBrandFilter(brand);
    chooseGeneration(generation);
    chooseMotorization(motorization);

    onVehicleFound();
  };

  const handleSkip = () => {
    setValidationMessage(undefined);
    setLookupError(null);
    onSkip();
  };

  return (
    <form onSubmit={handleSubmit} className={"flex flex-col my-4"}>
      <div className={"flex items-center gap-1.5 text-sm"}>
        <label htmlFor={"contractNumber"}>{t("contractNumberStep.label")}</label>
        <Popover>
          <PopoverButton
            aria-label={helpTitle}
            className={"flex items-center text-base text-primary cursor-pointer"}
          >
            <QuestionMarkCircleIcon />
          </PopoverButton>
          <PopoverPanel
            anchor={{ to: "bottom start", gap: 8, padding: 16 }}
            className={
              "z-20 max-h-[85vh] w-[26rem] max-w-[92vw] overflow-y-auto rounded-lg border border-neutral-300 bg-white p-4 shadow-lg font-sans"
            }
          >
            <p className={"mb-1 font-bold"}>{helpTitle}</p>
            <p className={"mb-3 text-sm text-neutral-500"}>{t("contractNumberStep.help.hint")}</p>
            <ContractCertificate />
          </PopoverPanel>
        </Popover>
      </div>

      <div className={"md:w-80 max-w-full"}>
        <Input
          id={"contractNumber"}
          name={"contractNumber"}
          className={"w-full"}
          placeholder={t("contractNumberStep.placeholder")}
          value={contractNumber}
          onChange={handleChange}
          maxLength={CONTRACT_NUMBER_LENGTH}
          autoComplete={"off"}
          disabled={loading}
          validationMessage={validationMessage ?? lookupError}
        />
      </div>

      <SubmitButton
        text={loading ? t("contractNumberStep.submitLoading") : t("contractNumberStep.submit")}
        type={"submit"}
        disabled={loading}
      />

      <button
        type={"button"}
        data-testid={"skip-contract-number"}
        onClick={handleSkip}
        disabled={loading}
        className={"mt-3 self-start text-primary underline cursor-pointer"}
      >
        {t("contractNumberStep.skip")}
      </button>
    </form>
  );
}
